import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { usePackages } from "../../../../../hooks/usePackages";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  CartesianGrid,
} from "recharts";
import { formatNumberCharts, handleMonth, translate } from "../../../../../utils/Common";
import Spinner from "../../../../../components/Spinner";
import { LuBox } from "react-icons/lu";
import ClientsSearchList from "../../../../../components/ClientsSearchList";
import { useForm } from "react-hook-form/dist/index.ie11";
import PlatformSearchList from "../../../../../components/PlatformSearchList";
import { verifyAccessViewPermissions } from "../../../../../config/permissions";
import Select from "../../../../../components/common/Select";
import styles from "./Charts.module.css";

// La API devuelve una fila por mes: el total, los clientes con más volumen y el resto
// agrupado en "otros". El eje X es el mes y el desglose vive en el tooltip. Una serie por
// cliente exigiría acotar a siete colores —y tres de ellos quedan bajo 3:1 sobre el fondo
// claro—, además de traerse un payload que crece con el número de clientes.
const toChartData = (rows) =>
  rows
    .map((row) => ({
      monthNumber: Number(row?.monthCurrent),
      month: handleMonth(row?.monthCurrent),
      total: Number(row?.result) || 0,
      clients: (row?.clients ?? []).map((client) => ({
        name: (client?.nameClient || "").trim(),
        total: Number(client?.result) || 0,
      })),
      othersCount: Number(row?.othersCount) || 0,
      othersResult: Number(row?.othersResult) || 0,
    }))
    .sort((a, b) => a.monthNumber - b.monthNumber);

const PackagesPickupMonthByClient = (props) => {
  const { yearSelect, canReadFilters = true } = props;
  const location = useLocation();
  const { getPickupMonthByClient } = usePackages();
  const { register, errors, clearErrors, setError, setValue } = useForm();

  const [data, setData] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [platformSelect, setPlatformSelect] = useState(null);
  const [typePackage, setTypePackage] = useState(2);
  const [isLoading, setIsLoading] = useState(false);

  // Quien no puede elegir cliente no manda ninguno y la API acota por el token.
  // Antes se mandaba el IdCliente del propio usuario: las cuentas BLI son de
  // IdTipoCliente 7 y ésas nunca figuran como destinatario de un paquete, así que
  // el gráfico salía vacío para todos ellos.
  const canPickClient =
    canReadFilters && verifyAccessViewPermissions(["search-clients"], { path: location.pathname });

  const getData = async () => {
    setIsLoading(true);
    const signal = new AbortController().signal;
    const result = await getPickupMonthByClient(signal, {
      year: Number(yearSelect),
      clientId: Number(selectedClient) || 0,
      lockerId: Number(platformSelect?.boxitLocationId) || 0,
      typePackage: Number(typePackage),
    });

    setIsLoading(false);

    if (!result.ok) {
      setHasError(true);
      setData(null);
      return;
    }

    setHasError(false);
    setData(toChartData(result.payload));
  };

  useEffect(() => {
    getData();
  }, [yearSelect, selectedClient, platformSelect, typePackage, canPickClient]);

  const typePackageOptions = [
    { value: "2", label: translate("global.status.typePackage.regular") },
    { value: "3", label: translate("global.status.typePackage.returned") },
  ];

  const ClientsTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const { total, clients, othersCount, othersResult } = payload[0].payload;

    return (
      <div className={styles.tooltipCard}>
        <p className={styles.tooltipTitle}>{`${label} ${yearSelect}`}</p>
        <p className={styles.tooltipTotal}>
          {`${translate("global.total")}: ${formatNumberCharts(total)}`}
        </p>
        <ul className={styles.tooltipList}>
          {clients.map((client, index) => (
            <li key={`${client.name}-${index}`}>
              <span>{client.name || translate("global.clients")}</span>
              <span>{formatNumberCharts(client.total)}</span>
            </li>
          ))}
          {othersCount > 0 && (
            <li>
              <span>{`${translate("global.charts.otherClients")} (${othersCount})`}</span>
              <span>{formatNumberCharts(othersResult)}</span>
            </li>
          )}
        </ul>
      </div>
    );
  };

  return (
    <div className={styles.chartSection}>
      <div className={styles.chartHeaderRow}>
        <h6 className={styles.chartTitle}>
          {isLoading ? <span className={styles.chartTitle}><Spinner type={1} /></span> : <LuBox size={20} aria-hidden />}
          {" "}
          {translate("global.packagesPickupXClient")} -{" "}
          <span className={styles.chartTitleAccent}>{yearSelect}</span>
        </h6>
        {canReadFilters && (
          <div className={styles.filterGroup}>
            <label htmlFor="inputTypePackage" className={styles.filterLabel}>
              <LuBox size={12} /> {translate("global.typeOfPackage")}
            </label>
            <Select
              id="inputTypePackage"
              value={String(typePackage)}
              onChange={(e) => setTypePackage(Number(e.target.value))}
              options={typePackageOptions}
              size="sm"
            />
          </div>
        )}
      </div>

      <hr className={styles.chartDivider} />

      {canReadFilters && (
        <div className={styles.chartFilters}>
          {canPickClient && (
          <div className={styles.selectWrapper} style={{ width: "250px" }}>
            <ClientsSearchList
              idUsuarioSelected={"all"}
              errors={errors}
              setError={setError}
              clearErrors={clearErrors}
              register={register}
              idTipoCliente={1}
              setSelectOption={setSelectedClient}
              isRequired={false}
            />
          </div>
          )}
          <div className={styles.selectWrapper} style={{ width: "200px" }}>
            <PlatformSearchList
              register={register}
              errors={errors}
              setPlatformSelect={setPlatformSelect}
              platfSelect={platformSelect}
              valueId={"plataforma"}
              readOnly={false}
              setValue={setValue}
              isClearable
            />
          </div>
        </div>
      )}

      {hasError ? (
        <div className={styles.noDataCard}>{translate("global.charts.loadError")}</div>
      ) : data === null ? null : data.length === 0 ? (
        <div className={styles.noDataCard}>{translate("global.noDataToView")}</div>
      ) : (
        <div className={styles.chartContainer}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
              <XAxis dataKey="month" stroke="var(--text-secondary)" />
              <YAxis tickFormatter={formatNumberCharts} stroke="var(--text-secondary)" />
              <Tooltip content={<ClientsTooltip />} cursor={{ fill: "var(--bg-tertiary)" }} />
              <Bar
                dataKey="total"
                fill="var(--color-primary)"
                maxBarSize={40}
                radius={[4, 4, 0, 0]}
                name={translate("global.packages")}
              >
                <LabelList
                  dataKey="total"
                  position="top"
                  fill="var(--text-primary)"
                  fontSize={12}
                  formatter={(value) => formatNumberCharts(value)}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default PackagesPickupMonthByClient;
